"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Calendar,
  Clock,
  DollarSign,
  Download,
  Printer,
  Send,
  Edit,
  MoreHorizontal,
  CreditCard,
  FileText,
  XCircle,
} from "lucide-react";
import type { QuickBooksInvoice } from "@/lib/types/quickbooks-unified";
import { SimpleCompanyBadge } from "@/components/quickbooks/entities/SimpleCompanyBadge";
import { PaymentStatusBadge } from "./InvoiceBadges";
import { formatCurrency, formatDate } from "../utils/invoiceFormatters";

interface InvoiceDetailsHeaderProps {
  invoice?: QuickBooksInvoice;
  onClose: () => void;
}

/**
 * Sticky header component for invoice details modal
 * Displays invoice summary, company badge, and action buttons
 */
export const InvoiceDetailsHeader: React.FC<InvoiceDetailsHeaderProps> = ({ invoice, onClose }) => {
  return (
    <div className="sticky top-0 z-10 -mx-4 px-4 py-2 bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60 border-b">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex flex-col">
            {/* Company Badge - Prominent at the top */}
            {invoice?.company && (
              <div className="mb-2">
                <SimpleCompanyBadge
                  company={invoice.company}
                  variant="default"
                  size="md"
                  className="font-semibold"
                />
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base md:text-lg font-semibold text-card-foreground">
                {invoice?.DocNumber ? `Invoice #${invoice.DocNumber}` : "Invoice"}
              </span>
              <PaymentStatusBadge balance={invoice?.Balance} total={invoice?.TotalAmt} />
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-gray-600">
              <div className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                <span className="text-xs md:text-sm">{formatDate(invoice?.TxnDate)}</span>
              </div>
              <Separator orientation="vertical" className="h-4" />
              <div className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                <span className="font-semibold text-sm md:text-base">
                  Due {formatDate(invoice?.DueDate)}
                </span>
              </div>
              <Separator orientation="vertical" className="h-4" />
              <div className="flex items-center gap-1 text-gray-800">
                <DollarSign className="h-3.5 w-3.5" />
                <span className="font-semibold text-sm md:text-base">
                  Total {formatCurrency(invoice?.TotalAmt)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" className="h-8 px-3 gap-1.5">
            <Download className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Download</span>
          </Button>
          <Button variant="outline" size="sm" className="h-8 px-3 gap-1.5">
            <Printer className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Print</span>
          </Button>
          <Button variant="outline" size="sm" className="h-8 px-3 gap-1.5">
            <Send className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Email</span>
          </Button>
          <Button variant="outline" size="sm" className="h-8 px-3 gap-1.5">
            <Edit className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Edit</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0" title="More options">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>
                <CreditCard className="h-4 w-4 mr-2" />
                Record Payment
              </DropdownMenuItem>
              <DropdownMenuItem>
                <FileText className="h-4 w-4 mr-2" />
                Duplicate Invoice
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-600">
                <XCircle className="h-4 w-4 mr-2" />
                Void Invoice
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};
